/* ==============================================================
   Cart Drawer V2 — Cross-sell custom element + cart refresh hooks
   Created 2026-05-27 — Inaya Impulse theme
   Hooks on document events emitted by theme.cart (Impulse):
     - 'cart:updated' (after any cart change)
     - 'ajaxProduct:added' (after add via product page)
     - 'drawerOpen' (when drawer opens)
   No theme.js modification required.
   ============================================================== */
(function () {
  'use strict';

  if (window.CartDrawerV2Loaded) return;
  window.CartDrawerV2Loaded = true;

  var i18n = {
    add: (window.Shopify && Shopify.locale === 'en') ? 'Add' : 'Add',
    added: 'Added'
  };
  // Try to read live i18n from a global injected by Liquid if available later.

  function moneyFormat(cents) {
    if (window.theme && typeof theme.Currency === 'object' && typeof theme.Currency.formatMoney === 'function') {
      return theme.Currency.formatMoney(cents, (window.theme.moneyFormat || '€{{ amount }}'));
    }
    var amount = (cents / 100).toFixed(2);
    return amount + ' €';
  }

  /* -----------------------------------------------------------
     Drawer empty-state attribute sync
     Sync data-cart-empty on the drawer root after any cart change
     so the CSS toggles show/hide work without theme.js modif.
  ----------------------------------------------------------- */
  function syncEmptyState() {
    var drawer = document.getElementById('CartDrawer');
    if (!drawer) return;
    fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        drawer.setAttribute('data-cart-empty', cart.item_count === 0 ? 'true' : 'false');
        // Refresh cross-sell anchor product id from last item
        var xsell = drawer.querySelector('cart-drawer-cross-sell');
        if (xsell) {
          var lastItem = cart.items[cart.items.length - 1];
          var newAnchor = lastItem ? String(lastItem.product_id) : '';
          var oldAnchor = xsell.getAttribute('data-anchor-product-id') || '';
          if (cart.item_count === 0) {
            xsell.setAttribute('hidden', '');
          } else {
            xsell.removeAttribute('hidden');
            if (newAnchor !== oldAnchor) {
              xsell.setAttribute('data-anchor-product-id', newAnchor);
              if (typeof xsell.refresh === 'function') xsell.refresh();
            }
          }
        }
      })
      .catch(function () { /* silent */ });
  }

  document.addEventListener('cart:updated', syncEmptyState);
  document.addEventListener('ajaxProduct:added', syncEmptyState);
  // Initial sync on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncEmptyState);
  } else {
    syncEmptyState();
  }

  /* -----------------------------------------------------------
     Cross-sell accordion : collapsed by default on mobile,
     expanded on desktop. Toggle on title click.
     Saves user preference in sessionStorage so it persists
     during the same browsing session (avoids forcing re-collapse
     after each cart update).
  ----------------------------------------------------------- */
  var MOBILE_MQ = '(max-width: 767px)';
  var STORAGE_KEY = 'cdv2_xsell_collapsed';

  function initXsellCollapse() {
    var xsellEls = document.querySelectorAll('.cdv2-xsell');
    if (xsellEls.length === 0) return;
    var isMobile = window.matchMedia(MOBILE_MQ).matches;
    var stored = null;
    try { stored = sessionStorage.getItem(STORAGE_KEY); } catch (e) {}
    xsellEls.forEach(function (el) {
      var shouldCollapse;
      if (stored === 'true') shouldCollapse = true;
      else if (stored === 'false') shouldCollapse = false;
      else shouldCollapse = true; // first visit default: collapsed (closed) everywhere
      el.setAttribute('data-collapsed', shouldCollapse ? 'true' : 'false');
      var toggle = el.querySelector('[data-cdv2-xsell-toggle]');
      if (toggle) toggle.setAttribute('aria-expanded', shouldCollapse ? 'false' : 'true');
    });
  }

  document.addEventListener('click', function (e) {
    var toggle = e.target.closest && e.target.closest('[data-cdv2-xsell-toggle]');
    if (!toggle) return;
    var xsell = toggle.closest('.cdv2-xsell');
    if (!xsell) return;
    var isCollapsed = xsell.getAttribute('data-collapsed') === 'true';
    var newState = isCollapsed ? 'false' : 'true';
    xsell.setAttribute('data-collapsed', newState);
    toggle.setAttribute('aria-expanded', newState === 'true' ? 'false' : 'true');
    try { sessionStorage.setItem(STORAGE_KEY, newState); } catch (err) {}
  });

  // Init on load + after every cart refresh (markup may be re-injected)
  document.addEventListener('cart:updated', initXsellCollapse);
  document.addEventListener('ajaxProduct:added', initXsellCollapse);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initXsellCollapse);
  } else {
    initXsellCollapse();
  }

  /* -----------------------------------------------------------
     <cart-drawer-cross-sell> custom element
     - Reads data-anchor-product-id, data-metafield, data-limit, data-recommendations-url
     - Tries metafield-based curation first (if anchor product page exposes it via /products/.js)
     - Falls back to product_recommendations endpoint (intent=complementary)
     - Renders cards in [data-cdv2-xsell-rail]
  ----------------------------------------------------------- */
  function CartDrawerCrossSell() {
    return Reflect.construct(HTMLElement, [], CartDrawerCrossSell);
  }
  CartDrawerCrossSell.prototype = Object.create(HTMLElement.prototype);
  CartDrawerCrossSell.prototype.constructor = CartDrawerCrossSell;
  Object.setPrototypeOf(CartDrawerCrossSell, HTMLElement);

  CartDrawerCrossSell.prototype.connectedCallback = function () {
    this._rail = this.querySelector('[data-cdv2-xsell-rail]');
    this._title = this.querySelector('.cdv2-xsell__title');
    this._isHydrating = false;
    this._currentAnchor = this.getAttribute('data-anchor-product-id') || '';
    if (this._currentAnchor) {
      this.refresh();
    }
  };

  CartDrawerCrossSell.prototype.refresh = function () {
    var self = this;
    var anchor = this.getAttribute('data-anchor-product-id');
    if (!anchor || this._isHydrating) return;
    this._isHydrating = true;
    this._currentAnchor = anchor;
    this.showSkeleton();

    var limit = parseInt(this.getAttribute('data-limit') || '3', 10);
    var mfPath = (this.getAttribute('data-metafield') || '').split('.');
    var recUrl = this.getAttribute('data-recommendations-url') || '/recommendations/products';

    self.fetchFromMetafield(anchor, mfPath, limit)
      .then(function (products) {
        if (products && products.length > 0) return products;
        return self.fetchFromRecommendations(anchor, recUrl, limit);
      })
      .then(function (products) {
        if (anchor !== self._currentAnchor) return; // anchor changed mid-fetch — discard
        self.renderCards(products || [], limit);
      })
      .catch(function () {
        self._rail.innerHTML = '';
      })
      .then(function () {
        self._isHydrating = false;
      });
  };

  CartDrawerCrossSell.prototype.showSkeleton = function () {
    if (!this._rail) return;
    var limit = parseInt(this.getAttribute('data-limit') || '3', 10);
    var html = '';
    for (var i = 0; i < limit; i++) {
      html += '<div class="cdv2-xsell__card cdv2-xsell__card--skeleton" aria-hidden="true">'
            + '<div class="cdv2-xsell__img"></div>'
            + '<div class="cdv2-xsell__line"></div>'
            + '<div class="cdv2-xsell__line cdv2-xsell__line--short"></div>'
            + '</div>';
    }
    this._rail.innerHTML = html;
  };

  CartDrawerCrossSell.prototype.fetchFromMetafield = function (anchorProductId, mfPath, limit) {
    // Try /products/{handle}.js — but we have ID only. Use /products/{id}.js via /products.json — not supported.
    // Workaround: hit the anchor product page metafield via Liquid is impossible from JS post-render.
    // Strategy: skip metafield lookup unless we can get the handle. We try fetching the cart item's product url via /cart.js.
    return fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var anchorItem = null;
        for (var i = cart.items.length - 1; i >= 0; i--) {
          if (String(cart.items[i].product_id) === String(anchorProductId)) {
            anchorItem = cart.items[i];
            break;
          }
        }
        if (!anchorItem || !anchorItem.handle) return [];
        return fetch('/products/' + anchorItem.handle + '.js', { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (product) {
            // Metafield surface via /products/X.js is LIMITED — Shopify exposes only some fields.
            // We rely on the metafield being indexable via the Storefront API or surfacing via product.metafields if shop has setup.
            // If not directly accessible from JSON endpoint, skip and let recommendations fallback take over.
            var mf = null;
            try {
              if (product && product.metafields && mfPath.length >= 2) {
                mf = product.metafields[mfPath[0]] && product.metafields[mfPath[0]][mfPath[1]];
              }
            } catch (e) {}
            if (!mf || !mf.value) return [];
            // metafield value should be a list of product references or handles
            var handles = [];
            if (Array.isArray(mf.value)) {
              handles = mf.value.map(function (v) { return (typeof v === 'string') ? v : (v.handle || ''); }).filter(Boolean);
            } else if (typeof mf.value === 'string') {
              handles = mf.value.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
            }
            if (handles.length === 0) return [];
            return Promise.all(handles.slice(0, limit).map(function (handle) {
              return fetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' } })
                .then(function (r) { return r.json(); })
                .catch(function () { return null; });
            })).then(function (products) { return products.filter(Boolean); });
          })
          .catch(function () { return []; });
      })
      .catch(function () { return []; });
  };

  CartDrawerCrossSell.prototype.fetchFromRecommendations = function (anchorProductId, recUrl, limit) {
    var url = recUrl + '?product_id=' + encodeURIComponent(anchorProductId) + '&limit=' + limit + '&intent=complementary&section_id=cart-drawer-cross-sell';
    // The recommendations endpoint can return either JSON or section HTML. We use the .json variant.
    var jsonUrl = recUrl + '.json?product_id=' + encodeURIComponent(anchorProductId) + '&limit=' + limit + '&intent=complementary';
    return fetch(jsonUrl, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data && data.products && data.products.length > 0) return data.products;
        // Try without intent (older Shopify versions)
        return fetch(recUrl + '.json?product_id=' + encodeURIComponent(anchorProductId) + '&limit=' + limit, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
          .then(function (r) { return r.json(); })
          .then(function (d) { return (d && d.products) ? d.products : []; });
      })
      .catch(function () { return []; });
  };

  CartDrawerCrossSell.prototype.renderCards = function (products, limit) {
    if (!this._rail) return;
    var anchor = String(this._currentAnchor);
    var filtered = products.filter(function (p) { return String(p.id) !== anchor; }).slice(0, limit);
    if (filtered.length === 0) {
      this.setAttribute('hidden', '');
      this._rail.innerHTML = '';
      return;
    }
    this.removeAttribute('hidden');
    var html = filtered.map(function (p) {
      var img = (p.featured_image && (p.featured_image.src || p.featured_image)) || (p.images && p.images[0]) || '';
      if (typeof img === 'object' && img.src) img = img.src;
      var price = (typeof p.price === 'number') ? p.price : (p.price_min || 0);
      var firstVariantId = (p.variants && p.variants[0] && p.variants[0].id) || p.id;
      return ''
        + '<div class="cdv2-xsell__card" data-cdv2-card="' + p.id + '">'
        + '  <a class="cdv2-xsell__img-wrap" href="' + (p.url || ('/products/' + (p.handle || ''))) + '">'
        + (img ? '<img src="' + img + '" alt="" loading="lazy" decoding="async">' : '')
        + '    <button type="button" class="cdv2-xsell__add" data-cdv2-add data-variant-id="' + firstVariantId + '" aria-label="Add">'
        + '      <svg class="cdv2-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14"/><path d="M12 5v14"/></svg>'
        + '    </button>'
        + '  </a>'
        + '  <a class="cdv2-xsell__name" href="' + (p.url || ('/products/' + (p.handle || ''))) + '">' + escapeHtml(p.title) + '</a>'
        + '  <span class="cdv2-xsell__price">' + moneyFormat(price) + '</span>'
        + '</div>';
    }).join('');
    this._rail.innerHTML = html;
    this.bindAddButtons();
  };

  CartDrawerCrossSell.prototype.bindAddButtons = function () {
    var self = this;
    var buttons = this._rail.querySelectorAll('[data-cdv2-add]');
    buttons.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var variantId = btn.getAttribute('data-variant-id');
        if (!variantId) return;
        btn.disabled = true;
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ id: variantId, quantity: 1 })
        })
          .then(function (r) { return r.json(); })
          .then(function () {
            btn.classList.add('is-added');
            // Trigger cart refresh via existing Impulse hook
            document.dispatchEvent(new CustomEvent('cart:updated'));
            // Some Impulse builds also listen on ajaxProduct:added
            document.dispatchEvent(new CustomEvent('ajaxProduct:added'));
            // Refresh injected cart markup if theme.cart exposes a method
            if (window.theme && window.theme.cart && typeof window.theme.cart.getCartProductMarkup === 'function' && typeof window.theme.cart.cartMarkup === 'function') {
              try { window.theme.cart.getCartProductMarkup().then(window.theme.cart.cartMarkup.bind(window.theme.cart)); } catch (e) {}
            }
          })
          .catch(function () {
            btn.disabled = false;
          });
      });
    });
  };

  function escapeHtml(s) {
    return String(s || '').replace(/[&<>"']/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
    });
  }

  if (typeof customElements !== 'undefined' && !customElements.get('cart-drawer-cross-sell')) {
    customElements.define('cart-drawer-cross-sell', CartDrawerCrossSell);
  }

})();
